export const LoginState = {
  NONE: 'NONE',
  FAILED_AUTHENTICATION: 'FAILED_AUTHENTICATION',
  USER_DEACTIVATED: 'USER_DEACTIVATED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_REPLACED: 'SESSION_REPLACED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  GENERIC_ERROR: 'GENERIC_ERROR',
  LOGGED_OUT: 'LOGGED_OUT',
  RESET_PASSWORD: 'RESET_PASSWORD',
  INVALID_OTP: 'INVALID_OTP',
  MFA_TOKEN_EXPIRED: 'MFA_TOKEN_EXPIRED',
  MFA_CODE_EXPIRED: 'MFA_CODE_EXPIRED',
  MFA_CODE_LOCKED: 'MFA_CODE_LOCKED',
  MFA_RESEND_TOO_SOON: 'MFA_RESEND_TOO_SOON',
  MFA_CODE_RESENT: 'MFA_CODE_RESENT'
}

/** Alertas de sistema/sesión: se muestran como notificación (toast), no dentro del formulario. */
export const LOGIN_TOAST_STATES = new Set([
  LoginState.LOGGED_OUT,
  LoginState.SESSION_EXPIRED,
  LoginState.SESSION_REPLACED,
  LoginState.RESET_PASSWORD
])

export function isLoginToastState(state) {
  return LOGIN_TOAST_STATES.has(state)
}

export const LOGIN_ALERTS = {
  [LoginState.FAILED_AUTHENTICATION]: {
    variant: 'error',
    message: 'Correo electrónico o contraseña inválidos.'
  },
  [LoginState.USER_DEACTIVATED]: {
    variant: 'error',
    message: 'Esta cuenta ha sido desactivada.'
  },
  [LoginState.SESSION_EXPIRED]: {
    variant: 'error',
    message: 'Tu sesión finalizó. Por favor inicia sesión nuevamente.'
  },
  [LoginState.SESSION_REPLACED]: {
    variant: 'warning',
    message: 'Tu sesión se cerró automáticamente porque se inició sesión en otro dispositivo con esta cuenta.'
  },
  [LoginState.ACCOUNT_LOCKED]: {
    variant: 'error'
  },
  [LoginState.GENERIC_ERROR]: {
    variant: 'error',
    message: 'No fue posible iniciar sesión. Intenta nuevamente.'
  },
  [LoginState.LOGGED_OUT]: {
    variant: 'success',
    message: 'Cerraste sesión correctamente.'
  },
  [LoginState.RESET_PASSWORD]: {
    variant: 'success',
    message: 'Su contraseña ha sido cambiada exitosamente. Ya puede iniciar sesión.'
  },
  [LoginState.INVALID_OTP]: {
    variant: 'error',
    message: 'Código de autenticación inválido. Intenta nuevamente.'
  },
  [LoginState.MFA_TOKEN_EXPIRED]: {
    variant: 'error',
    message: 'El código de verificación expiró. Inicia sesión otra vez.'
  },
  [LoginState.MFA_CODE_EXPIRED]: {
    variant: 'error',
    message: 'El código expiró. Pide uno nuevo o inicia sesión otra vez.'
  },
  [LoginState.MFA_CODE_LOCKED]: {
    variant: 'error',
    message: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.'
  },
  [LoginState.MFA_RESEND_TOO_SOON]: {
    variant: 'warning',
    message: 'Espera un momento antes de pedir otro código.'
  },
  [LoginState.MFA_CODE_RESENT]: {
    variant: 'success',
    message: 'Te enviamos un código nuevo a tu correo.'
  }
}

export function getLoginErrorState(status, reason) {
  switch (reason) {
    case 'account_locked':
      return LoginState.ACCOUNT_LOCKED
    case 'user_inactive':
      return LoginState.USER_DEACTIVATED
    case 'token_expired':
    case 'invalid_token':
    case 'session_idle_timeout':
    case 'session_absolute_timeout':
      return LoginState.SESSION_EXPIRED
    case 'session_replaced':
      return LoginState.SESSION_REPLACED
    case 'invalid_credentials':
      return LoginState.FAILED_AUTHENTICATION
    case 'invalid_otp':
    case 'invalid_mfa_code':
      return LoginState.INVALID_OTP
    case 'mfa_token_expired':
      return LoginState.MFA_TOKEN_EXPIRED
    case 'mfa_code_expired':
      return LoginState.MFA_CODE_EXPIRED
    case 'mfa_code_locked':
      return LoginState.MFA_CODE_LOCKED
    case 'mfa_resend_too_soon':
      return LoginState.MFA_RESEND_TOO_SOON
    default:
      if (status === 423) return LoginState.ACCOUNT_LOCKED
      if (status === 403) return LoginState.USER_DEACTIVATED
      if (status === 401) return LoginState.FAILED_AUTHENTICATION
      return LoginState.GENERIC_ERROR
  }
}

export function getLoginAlertMessage(loginState, retryAfterSeconds) {
  const alertConfig = LOGIN_ALERTS[loginState]
  if (!alertConfig) return null

  if (loginState === LoginState.ACCOUNT_LOCKED) {
    return `Se bloqueó temporalmente el acceso a tu cuenta debido a varios intentos fallidos. ${
      retryAfterSeconds
        ? `Intenta iniciar sesión nuevamente en ${Math.max(1, Math.ceil(retryAfterSeconds / 60))} minutos.`
        : 'Intenta nuevamente más tarde.'
    }`
  }

  return alertConfig.message
}
